import { motion } from "framer-motion";

const ease = [0.22, 1, 0.36, 1];

/* Image tile — real photo, object-cover */
function Tile({ src, alt, shadow, className = "" }) {
  return (
    <div className={`relative overflow-hidden rounded-[22px] ring-4 ring-white ${shadow} ${className}`}>
      <img src={src} alt={alt} className="h-full w-full object-cover" loading="lazy" />
    </div>
  );
}

export default function HeroVisual() {
  return (
    <div className="relative w-full max-w-md lg:max-w-lg mx-auto lg:mx-0 aspect-[824/700]">
      {/* Decorative dotted pattern — behind everything, top-left */}
      <div
        className="absolute top-[10%] left-[-4%] h-[28%] w-[24%] opacity-40 pointer-events-none z-0"
        style={{
          backgroundImage: "radial-gradient(#C9A227 1.5px, transparent 1.5px)",
          backgroundSize: "13px 13px",
        }}
      />

      {/* Image A — main, large, top-right, base layer */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease, delay: 0.1 }}
        className="absolute z-[1]"
        style={{ left: "24%", top: "0%", width: "68.2%", height: "62%" }}
      >
        <Tile
          src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?fm=jpg&q=80&w=1200&auto=format&fit=crop"
          alt="Financial advisor reviewing plans with a client"
          shadow="shadow-[0_28px_60px_-18px_rgba(15,23,42,0.5)]"
          className="h-full w-full"
        />
      </motion.div>

      {/* Image B — small tile, overlaid on top of A's bottom-left corner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease, delay: 0.35 }}
        className="absolute z-[3]"
        style={{ left: "0%", top: "42%", width: "31.8%", height: "24.4%" }}
      >
        <Tile
          src="https://images.unsplash.com/photo-1521791136064-7986c2920216?fm=jpg&q=80&w=800&auto=format&fit=crop"
          alt="Advisor and client shaking hands on an agreement"
          shadow="shadow-[0_22px_45px_-16px_rgba(15,23,42,0.45)]"
          className="h-full w-full"
        />
      </motion.div>

      {/* Image C — wide tile, overlaid on top of A's bottom edge */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.85, ease, delay: 0.45 }}
        className="absolute z-[2]"
        style={{ left: "6%", top: "54%", width: "62%", height: "38.1%" }}
      >
        <Tile
          src="https://images.unsplash.com/photo-1758873269317-51888e824b28?fm=jpg&q=80&w=1200&auto=format&fit=crop"
          alt="Diverse team collaborating in the office"
          shadow="shadow-[0_24px_48px_-16px_rgba(15,23,42,0.45)]"
          className="h-full w-full"
        />
      </motion.div>

      {/* Image D — small tile, overlaid on top of C's right edge */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease, delay: 0.6 }}
        className="absolute z-[4]"
        style={{ left: "60%", top: "70%", width: "31.8%", height: "26%" }}
      >
        <div className="h-full w-full relative">
          <Tile
            src="https://images.unsplash.com/photo-1778961419928-2968ddd57c05?fm=jpg&q=80&w=800&auto=format&fit=crop"
            alt="Modern office building"
            shadow="shadow-[0_22px_45px_-16px_rgba(15,23,42,0.45)]"
            className="h-full w-full"
          />
          {/* small decorative accent leaf */}
          <div className="absolute -right-2 bottom-3 h-4 w-4 rounded-sm bg-[#C9A227] rotate-45" aria-hidden />
        </div>
      </motion.div>

      {/* Badge — speech-bubble style, floats above everything */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease, delay: 0.75 }}
        className="absolute z-[5] w-[38%] min-w-[100px] max-w-[135px]"
        style={{ left: "14%", top: "6.1%" }}
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.9 }}
          className="relative"
        >
          <div className="rounded-[16px] bg-[#C9A227] text-[#0F172A] px-3 py-3 shadow-[0_20px_45px_-15px_rgba(201,162,39,0.5)]">
            <div className="text-[13px] sm:text-[14px] md:text-[15px] font-display leading-[1.15] font-bold">
              Trusted Partner
            </div>
            <div className="text-[10px] sm:text-[11px] font-semibold mt-1.5 leading-snug text-white">
              Since day one
            </div>
          </div>
          {/* speech-bubble tail */}
          <div className="absolute -bottom-1.5 left-5 h-3.5 w-3.5 bg-[#C9A227] rotate-45 rounded-[3px]" aria-hidden />
        </motion.div>
      </motion.div>
    </div>
  );
}